/*@flow*/
import type { Element } from 'react';
import * as I from 'immutable';
import React, { useEffect, useState, useContext } from 'react';
import Folio from 'lib/folio';
import Blank from 'lib/Blank';
import hash from 'lib/hash';
import basemaps from 'lib/basemaps';
import waitTill from 'lib/waitTill';
import context from './context';
import commands from './commands';
import DrawControl from './DrawControl';

let decks = {
  basemap: {
    type: 'vector',
    url: basemaps.quiet,
    access_token: ENV.MAPBOX_ACCESS_TOKEN,
  },
  zoom: {
    type: 'zoom',
    position: 'bottomright',
  },
  curbs: {
    type: 'cdb',
    sql: 'SELECT * FROM geo_export_ed1eb0e3_67ee_44cc_acf1_9040b750fa6c',
    cartocss: '#layer {line-width: 0.5;line-color: #000000;line-opacity: 1;}',
  },
  properties: {
    type: 'cdb',
    sql: 'SELECT *, ST_AsGeoJSON(the_geom) as geojson FROM mappluto',
    cartocss:
      "#layer {polygon-fill: #826dba;polygon-opacity: 0;} #layer::outline {line-width: 0.5;line-color: #ff6804;line-opacity: 0.9;} #layer::labels {text-name: [lot];text-face-name: 'DejaVu Sans Book';text-size: 10;text-fill: #ff6804;text-label-position-tolerance: 0;text-halo-radius: 0;text-halo-fill: #ffffff;text-dy: 0;text-allow-overlap: true;text-placement: point;text-placement-type: dummy; [zoom <= 17] {text-size: 0} [zoom >= 18] {text-size: 08}}",
    interactivity: 'address,geojson',
  },
  renoProperties: {
    type: 'cdb',
    sql: 'SELECT *, ST_AsGeoJSON(the_geom) as geojson FROM reno_parcels',
    cartocss:
      '#layer {polygon-fill: #826dba;polygon-opacity: 0;} #layer::outline {line-width: 0.5;line-color: #ff6804;line-opacity: 0.9;}',
    interactivity: 'geojson',
  },
};

const getMarker = (latlon) =>
  I.fromJS({
    type: 'marker',
    latlon,
    iconOptions: true,
  });

// When the center changes in the state, update the center in the schema.
export const useCenterSync = () => {
  const ctx = useContext(context);
  const { center } = ctx.state;
  useEffect(() => {
    if (!center) return;
    if (Blank.is(center)) return;
    const { lat, lon } = center;

    function setCenter() {
      commands(ctx).set('schema.center', I.List([lat, lon]));
    }
    if (window.propertyMap) {
      const mapBounds = window.propertyMap.getBounds();
      const centerInView = mapBounds.contains(L.latLng(lat, lon));
      !centerInView && setCenter();
    } else {
      waitTill(() => window.propertyMap).then(() => {
        window.propertyMap.invalidateSize();
        setCenter();
      });
    }

    return () => {
      window.propertyMap = null;
    };
  }, [hash(center)]);
};

// Attach the event binder to map to track the clicked locations.
export const useMapClickSync = () => {
  const ctx = useContext(context);
  const { mode } = ctx.state;
  const [point, setPoint] = useState();

  useEffect(() => {
    if (!point) return;
    if (mode !== 'READY' && mode !== 'DRAWING') return;
    commands(ctx).setLocation(point);
    commands(ctx).set('decks.marker', getMarker(point));
  }, [hash(point), mode]);

  useEffect(() => {
    const onClick = ({ e } = {}) => {
      if (!e) return;
      const { lat, lng } = e.latlng;
      setPoint([lat, lng]);
    };
    const updateOnClickHandler = (s) => {
      if (!s) return;
      return s.setIn(['on', 'click'], onClick);
    };
    commands(ctx).set('schema', updateOnClickHandler);
  }, []);
};

// When theGeom changes in the state, update the 'shape' deck. Shape deck
// can display a point or a polygon, depending on the value of theGeom.
export const useShapeSync = () => {
  const ctx = useContext(context);
  const { theGeom, mode } = ctx.state;

  useEffect(() => {
    if (!theGeom) return;
    commands(ctx).setShape();
  }, [theGeom, mode]);
};

const InfoControl = () => {
  const ctx = useContext(context);
  const { mode } = ctx.state;
  if (mode === 'DRAWING') return null;
  return (
    <div className="text-black-50">Click anywhere to change the location.</div>
  );
};

const useMapInit = () => {
  const ctx = useContext(context);

  const schema = {
    type: 'map',
    center: [40.712654662428214, -74.00588035583496],
    zoom: 19,
    maxZoom: 22,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    on: {
      dragend: (e) => {
        const { lat, lng } = e.e.target.getCenter();
        // Update schema center on dragend.
        // NOTE: This could be done by Folio.
        commands(ctx).set('schema.center', I.List([lat, lng]));
      },
      zoomend: (e) => {
        const zoom = e.e.target.getZoom();
        commands(ctx).set('schema.zoom', zoom);
      },
    },
  };

  useEffect(() => {
    decks = {
      ...decks,
      drawPoly: {
        type: 'draw',
        active: false,
        ready(handler) {
          commands(ctx).set('drawingHandler', handler);
        },
      },
      drawBtn: {
        type: 'control',
        className: 'draw-btn-control',
        content: () => <DrawControl />,
        position: 'topright',
      },
      infoControl: {
        type: 'control',
        className: 'info-control',
        content: () => <InfoControl />,
        position: 'bottomleft',
      },
    };

    commands(ctx).set('schema', I.fromJS(schema));
    commands(ctx).set('decks', I.fromJS(decks));
  }, []);
};

const Component = (): null | Element<'div'> => {
  const ctx = useContext(context);
  const { theGeom } = ctx.state;

  useMapInit();
  useMapClickSync();
  useCenterSync();
  useShapeSync();

  if (!theGeom) return null;
  return (
    <div
      className="w-100 position-relative"
      style={{ height: 200, overflow: 'hidden' }}
    >
      <Folio
        name="propertyMap"
        schema={ctx.state.schema}
        decks={ctx.state.decks}
        loading={false}
      />
    </div>
  );
};

export default Component;
